import { useEffect, useState } from "react";
import { Loader2, Sparkles, Milestone, BookMarked, Award, HelpCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getCharacterCard, characterImageUrl } from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

function Section({ icon: Icon, label, children }) {
  return (
    <div className="mb-5">
      <p className="font-ui mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      {children}
    </div>
  );
}

// Shared character detail dialog. Pass `character` ({id, name, tag?}) to open; null to close.
export default function CharacterCardDialog({ character, onOpenChange, showExploreLink = false }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (!character) return;
    setCard(null); setError(false); setLoading(true); setImgFailed(false);
    getCharacterCard(character.id)
      .then(setCard)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [character]);

  return (
    <Dialog open={!!character} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="font-ui max-h-[85vh] overflow-y-auto bg-background p-0 sm:max-w-lg" data-testid="character-card-dialog">
        {character && !imgFailed && (
          <div className="relative h-44 w-full overflow-hidden rounded-t-lg bg-gradient-to-br from-[#8A5A44] to-[#2D1B36]">
            <img
              src={characterImageUrl(character.id)}
              alt={character.name}
              onError={() => setImgFailed(true)}
              className="h-full w-full object-cover object-top"
              data-testid="character-portrait"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          </div>
        )}
        <div className={`px-6 pb-6 ${character && !imgFailed ? "-mt-10 relative" : "pt-6"}`}>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{character?.name}</DialogTitle>
          <DialogDescription>{card?.title || character?.tag || "Bible character"}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Preparing this profile…<br />(first time takes a few seconds)</p>
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Couldn’t load this profile right now. Please try again shortly.</p>
        ) : card ? (
          <div className="mt-4" data-testid="character-card-content">
            {card.era && <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-primary">{card.era}</p>}
            <Section icon={Sparkles} label="Who they were">
              <p className="reading-text text-sm leading-relaxed text-foreground/90">{card.summary}</p>
            </Section>
            {card.traits?.length > 0 && (
              <div className="mb-5 flex flex-wrap gap-2">
                {card.traits.map((t, i) => <span key={i} className="rounded-full bg-muted px-3 py-1 text-xs">{t}</span>)}
              </div>
            )}
            {card.key_events?.length > 0 && (
              <Section icon={Milestone} label="Key moments">
                <ul className="space-y-2">
                  {card.key_events.map((e, i) => <li key={i} className="reading-text text-sm leading-relaxed text-foreground/90">• {e}</li>)}
                </ul>
              </Section>
            )}
            {card.key_verses?.length > 0 && (
              <Section icon={BookMarked} label="Key verses">
                <div className="flex flex-wrap gap-2">
                  {card.key_verses.map((v, i) => <span key={i} className="rounded-lg bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{v}</span>)}
                </div>
              </Section>
            )}
            {card.legacy && (
              <Section icon={Award} label="Legacy">
                <p className="reading-text text-sm leading-relaxed text-foreground/90">{card.legacy}</p>
              </Section>
            )}
            {card.reflection && (
              <div className="rounded-2xl bg-primary/10 p-5">
                <p className="font-ui mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                  <HelpCircle className="h-3.5 w-3.5" /> Reflect
                </p>
                <p className="reading-text text-sm italic leading-relaxed text-foreground/90">{card.reflection}</p>
              </div>
            )}
            {showExploreLink && (
              <Link
                to="/characters"
                data-testid="character-explore-all"
                className="font-ui mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                Explore all characters <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
